import React from 'react';
import {IconButton} from "@mui/joy";
import {SxProps} from "@mui/joy/styles/types";
import {StarFilledRounded, StarRounded} from "@mui-symbols-material/w300";
import {useMutation} from "@apollo/client";
import {UpdateStarredInstalledBaseNode} from "../../graphql/mutations/updateStarredInstalledBaseNode";
import {toast} from "react-toastify";
import {resolveErrorMessage} from "../../utils/resolveErrorMessage";
import {useTranslation} from "react-i18next";

interface Props {
    nodeId: string | null | undefined;
    isStarred: boolean;
    variant?: 'plain' | 'outlined';
    sx?: SxProps;
}

const NodeFavoriteButton = ({nodeId, isStarred, variant = 'plain', sx}: Props) => {
    const {t} = useTranslation();

    const [updateStarred] = useMutation(UpdateStarredInstalledBaseNode, {
        optimisticResponse: {
            updateStarredInstalledBaseNode: {
                __typename: 'InstalledBaseNode',
                id: nodeId,
                isStarred: !isStarred,
            },
        },
        refetchQueries: ['StarredInstalledBaseNodeList'],
    });

    const handleToggleFavorite = () => {
        if (!nodeId) return;

        updateStarred({
            variables: {
                id: nodeId,
                starred: !isStarred,
            },
        }).then(() => {
            toast.success(isStarred ? t('Removed from favorites') : t('Added to favorites'));
        }).catch((err) => {
            toast.error(resolveErrorMessage(err, t));
        });
    };

    return (
        <IconButton
            variant={variant}
            color="neutral"
            sx={sx}
            disabled={!nodeId}
            aria-label={isStarred ? t('Remove from favorites') : t('Add to favorites')}
            title={isStarred ? t('Remove from favorites') : t('Add to favorites')}
            onClick={handleToggleFavorite}
        >
            {isStarred
                ? <StarFilledRounded/>
                : <StarRounded/>
            }
        </IconButton>
    );
};

export default NodeFavoriteButton;
